import { stringify } from 'query-string';
import { MutationFetcher } from 'swr/mutation';

import { OperatorEnum } from 'MODULES/universal-filters/utils/operators';
import { apiUrl } from 'SRC/config';

import { del, get, patch, post } from '../fetch';
import { FilterValues, MeasureFormatEnum } from './observability';

export type MonitorType = 'THRESHOLD' | 'ANOMALY';
export type ThresholdDirection = 'ABOVE' | 'BELOW';

export const MonitorStatus = {
  TRIGGERED: 'TRIGGERED',
  RECOVERED: 'RECOVERED',
  NO_DATA: 'NO_DATA',
} as const;
export type MonitorStatus = (typeof MonitorStatus)[keyof typeof MonitorStatus];

export type Monitor = {
  pk: string;
  type: MonitorType;
  aggregationWindow: number;
  name: string;
  measureId: string;
  measureName: string;
  measureFormat?: MeasureFormatEnum;
  filters: FilterValues[];
  minNumSamples?: number;
  statusSummary: { timestamp: string; status: MonitorStatus }[];
  currentStatus: MonitorStatus;
  thresholdDirection?: ThresholdDirection;
  thresholdValue?: number;
};

type MonitorEventCommons = {
  expectedValue: number;
  filters?: FilterValues[];
  id: string;
  measuredValue: number;
  measureFormat: MeasureFormatEnum;
  measureKey: string;
  measureMeta: Record<string, string[]>;
  measureName: string;
  merchantAccount: number;
  minNumSamples?: number;
  /**
   * monitor id
   */
  monitor: string;
  name: string;
  numSamples: number;
  // Check if we can make the BE consistent and return this uppercased
  operator: 'above' | 'below';
  /**
   * ISO Date string of when the monitor was triggered
   * !this is different from eventWindowEnd
   */
  triggeredAt: string;
  /**
   * ISO Date string
   */
  eventWindowEnd: string;
};

type AnomalyMonitorEvent = MonitorEventCommons & {
  type: 'ANOMALY';
  lowerBound: number;
  upperBound: number;
  aggregationWindow: null;
  eventWindowStart: null;
};

type ThresholdMonitorEvent = MonitorEventCommons & {
  type: 'THRESHOLD';
  lowerBound: null;
  upperBound: null;
  aggregationWindow: number;
  eventWindowStart: string;
};

export type MonitorEvent = AnomalyMonitorEvent | ThresholdMonitorEvent;

export type PostFilterValues = {
  filter: string;
  filterKey?: string;
  operator: OperatorEnum;
  values?: boolean | string[];
};

export type PostMonitor = {
  pk: string;
  name: string;
  type: MonitorType;
  cubejsMeasure: string;
  description?: string;
  aggregationWindow: number;
  thresholdDirection: ThresholdDirection;
  thresholdValue: number;
  minNumSamples: number;
  cubejsFilters: PostFilterValues[];
};

export type MonitorResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: Monitor[];
};
export const MONITORS = '/observability/monitors';
export const MONITORS_SUMMARY = '/observability/monitors/summary';

export const RECOMMENDED_MONITORS = '/observability/monitors/recommended';
export const MONITORS_EVENTS = 'observability/monitors/events';
export const MONITORS_RESULTS = 'observability/monitors/results';

export const fetchMonitors = () =>
  get<MonitorResponse>(apiUrl + MONITORS, {}, { auth: true });

export const fetchMonitorsSummary = () =>
  get<{ triggeredMonitorsLastHour: number }>(
    apiUrl + MONITORS_SUMMARY,
    {},
    { auth: true },
  );

export const fetchMonitorDetails = (id: string) =>
  get<Monitor>(`${apiUrl}${MONITORS}/${id}`, {}, { auth: true });
export interface FetchMonitorsEventsData {
  count: number;
  next: string | null;
  previous: string | null;
  results: MonitorEvent[];
}

export const fetchMonitorsEvents = (
  cursor?: string,
  monitor?: string,
  fromDate?: string,
  toDate?: string,
) =>
  get<FetchMonitorsEventsData>(
    `${apiUrl}/${MONITORS_RESULTS}?${stringify({
      cursor_pagination: 'true',
      page_size: 100,
      cursor,
      monitor,
      from_date: fromDate,
      to_date: toDate,
    })}`,
    {},
    { auth: true },
  );

export const fetchMonitorEvent = (id: string) =>
  get<MonitorEvent>(`${apiUrl}/${MONITORS_RESULTS}/${id}`, {}, { auth: true });

export const postMonitor: MutationFetcher<
  unknown,
  string,
  Partial<PostMonitor>
> = (_url, { arg }) =>
  post(apiUrl + MONITORS, { body: JSON.stringify(arg) }, { auth: true });

export const updateMonitor: MutationFetcher<
  unknown,
  string,
  Partial<PostMonitor>
> = (_url, { arg: { pk, ...rest } }) =>
  patch(
    `${apiUrl}${MONITORS}/${pk ?? ''}`,
    { body: JSON.stringify(rest) },
    { auth: true },
  );

export const deleteMonitor: MutationFetcher<unknown, string, string> = (
  _url,
  { arg },
) => {
  return del(`${apiUrl}${MONITORS}/${arg}`, {}, { auth: true });
};

export const fetchRecommendedMonitors = () =>
  get<MonitorResponse>(`${apiUrl}${RECOMMENDED_MONITORS}`, {}, { auth: true });
